import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, Building, Sparkles, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { setCredentials } from '../store/authSlice';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaGoogle } from 'react-icons/fa';

const RecruiterRegisterPageContent = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const passwordRegex = /^(?=.*\d).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError('Le mot de passe doit contenir au moins 8 caractères et un chiffre');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setIsLoading(false);
      return;
    }

    if (!formData.company_name.trim()) {
      setError('Le nom de l\'entreprise est requis');
      setIsLoading(false);
      return;
    }

    try {
      const registerResponse = await axios.post(`${process.env.REACT_APP_BASE_URL}/recruiter/register`, {
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name
      });

      if (registerResponse.data && registerResponse.status === 201) {
        // Create session immediately
        const { token, user } = registerResponse.data;
        if (token && user) {
          dispatch(setCredentials({ user, token }));
        }

        // Send verification email
        try {
          await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/send-verification`, {
            email: formData.email,
            userId: registerResponse.data.user?.id || registerResponse.data.userId,
            userName: formData.company_name
          });
        } catch (e) { console.error(e) }

        navigate('/verify-email', { state: { email: formData.email } });
      } else {
        throw new Error('Registration response was not successful');
      }
    } catch (err) {
      console.error("Registration Error:", err);

      if (err.response?.status === 400) {
        if (err.response.data?.error?.includes('Google')) {
          setError('Cet email est déjà utilisé via un compte Google. Veuillez vous connecter avec Google.');
        } else {
          setError('Un compte avec cette adresse email existe déjà.');
        }
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Une erreur est survenue lors de l\'inscription');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (tokenResponse) => {
    setError('');
    setIsLoading(true);
    try {
      const backendResponse = await axios.post(`${process.env.REACT_APP_BASE_URL}/recruiter/auth/google-signup`, {
        googleAccessToken: tokenResponse.access_token,
      });

      const { token, user } = backendResponse.data;
      dispatch(setCredentials({ user, token }));
      navigate('/dashboard');
    } catch (err) {
      console.error("Google Sign-In Backend Error:", err);
      if (err.response?.status === 400) {
        setError('Cet email est déjà utilisé. Veuillez vous connecter.');
      } else {
        setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'inscription avec Google.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginError = (errorResponse) => {
    console.error("Google login failed directly:", errorResponse);
    setError('Échec de la connexion avec Google. Veuillez réessayer.');
    setIsLoading(false);
  };

  let googleLoginFlow = () => {
    console.error("useGoogleLogin hook is not available");
    setError("Le module de connexion Google n'a pas pu être chargé.");
  };

  try {
    const actualUseGoogleLogin = useGoogleLogin;
    if (typeof actualUseGoogleLogin === 'function') {
      googleLoginFlow = actualUseGoogleLogin({
        onSuccess: handleGoogleLoginSuccess,
        onError: handleGoogleLoginError,
      });
    }
  } catch (e) {
    console.error("Error initializing useGoogleLogin:", e);
  }

  const triggerGoogleLogin = () => {
    if (isLoading) return;
    googleLoginFlow();
  };

  const features = [
    "Publication d'offres illimitées",
    "Accès à notre base de candidats",
    "Outils de matching IA",
    "Tableau de bord analytics",
    "Support prioritaire 24/7"
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Hero */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 relative overflow-hidden"
      >
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
          />
        </div>

        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4" />
                Rejoignez Tekhdem
              </div>
              <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
                Trouvez les Meilleurs Talents
              </h2>
              <p className="text-white text-xl mb-8 leading-relaxed">
                La plateforme N°1 de recrutement en Algérie. Créez votre compte et accédez à des milliers de candidats qualifiés.
              </p>

              {/* Features */}
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3 text-white"
                  >
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="inline-flex items-center gap-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-4 py-2 rounded-full mb-4"
            >
              <Sparkles className="w-4 h-4" />
              Inscription Recruteur
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Créer un compte
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Commencez à recruter dès aujourd'hui
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 p-4 rounded-r-xl mb-6"
            >
              <p className="font-semibold text-sm">Erreur</p>
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {/* Google Signup */}
          <button
            type="button"
            onClick={triggerGoogleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl
                      text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800
                      focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-300
                      disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md mb-6"
          >
            <FaGoogle className="w-5 h-5" />
            S'inscrire avec Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 rounded-md">ou</span>
            </div>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Company Name */}
            <div>
              <label htmlFor="company_name" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Nom de l'entreprise
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="company_name"
                  id="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="Votre entreprise"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 px-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Inscription en cours...
                </>
              ) : (
                <>
                  Créer mon compte
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vous avez déjà un compte ?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
const RecruiterRegisterPage = () => {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <RecruiterRegisterPageContent />
    </GoogleOAuthProvider>
  );
};

export default RecruiterRegisterPage;